type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const EMAIL_RE = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;

const ERROR_CLASS = 'formfield-error';
const INVALID_CLASS = 'is-invalid';

const MESSAGES = {
  required: 'To pole jest wymagane.',
  email: 'Podaj poprawny adres e-mail.',
  checkbox: 'Zaznaczenie tego pola jest wymagane.',
} as const;

const getFieldMessage = (field: FormField, dataKey: string, fallback: string): string =>
  field.dataset[dataKey] || fallback;

export const validRequired = (value: string): boolean => value.trim().length > 0;

export const validRequiredEmail = (value: string): boolean =>
  EMAIL_RE.test(value.trim());

export const validEmail = (value: string): boolean =>
  value.trim().length === 0 ? true : validRequiredEmail(value);

const getErrorId = (field: FormField): string => {
  if (!field.id) {
    field.id = `ff-${Math.random().toString(36).slice(2, 9)}`;
  }
  return `${field.id}-error`;
};

const clearFieldError = (field: FormField): void => {
  field.removeAttribute('aria-invalid');
  field.classList.remove(INVALID_CLASS);
  const describedBy = field.getAttribute('aria-describedby');
  if (describedBy) {
    const errorId = `${field.id}-error`;
    const cleaned = describedBy
      .split(/\s+/)
      .filter((id) => id !== errorId)
      .join(' ');
    if (cleaned) {
      field.setAttribute('aria-describedby', cleaned);
    } else {
      field.removeAttribute('aria-describedby');
    }
  }
  const parent = field.parentElement;
  if (parent) {
    const existing = parent.querySelector(`.${ERROR_CLASS}[id="${field.id}-error"]`);
    if (existing) existing.remove();
  }
};

const setFieldError = (field: FormField, message: string): void => {
  field.setAttribute('aria-invalid', 'true');
  field.classList.add(INVALID_CLASS);

  const errorId = getErrorId(field);
  const existingDescribedBy = field.getAttribute('aria-describedby');
  if (!existingDescribedBy || !existingDescribedBy.split(/\s+/).includes(errorId)) {
    field.setAttribute(
      'aria-describedby',
      existingDescribedBy ? `${existingDescribedBy} ${errorId}` : errorId,
    );
  }

  const parent = field.parentElement;
  if (!parent) return;

  let errorEl = parent.querySelector<HTMLParagraphElement>(
    `.${ERROR_CLASS}[id="${errorId}"]`,
  );
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = ERROR_CLASS;
    errorEl.id = errorId;
    parent.appendChild(errorEl);
  }
  errorEl.textContent = message;
};

const clearFormErrors = (form: HTMLFormElement): void => {
  form
    .querySelectorAll<FormField>('input, select, textarea')
    .forEach(clearFieldError);
};

const validateField = (field: FormField): true | string => {
  const type = field.getAttribute('type');
  const isRequired = field.hasAttribute('required');

  if (type === 'submit' || type === 'button' || type === 'hidden' || type === 'reset') {
    return true;
  }

  if (type === 'checkbox') {
    if (isRequired && !(field as HTMLInputElement).checked) {
      return getFieldMessage(field, 'fieldErrorRequired', MESSAGES.checkbox);
    }
    return true;
  }

  if (type === 'email') {
    if (isRequired && !validRequiredEmail(field.value)) {
      return validRequired(field.value)
        ? getFieldMessage(field, 'fieldErrorFormat', MESSAGES.email)
        : getFieldMessage(field, 'fieldErrorRequired', MESSAGES.required);
    }
    if (!validEmail(field.value)) {
      return getFieldMessage(field, 'fieldErrorFormat', MESSAGES.email);
    }
    return true;
  }

  if (isRequired && !validRequired(field.value)) {
    return getFieldMessage(field, 'fieldErrorRequired', MESSAGES.required);
  }

  return true;
};

const validateForm = (form: HTMLFormElement): boolean => {
  clearFormErrors(form);

  let firstInvalid: FormField | null = null;
  const fields = form.querySelectorAll<FormField>('input, select, textarea');

  fields.forEach((field) => {
    const result = validateField(field);
    if (result !== true) {
      setFieldError(field, result);
      if (!firstInvalid) firstInvalid = field;
    }
  });

  if (firstInvalid) {
    (firstInvalid as FormField).focus();
    return false;
  }

  return true;
};

const onDocumentSubmit = (e: Event): void => {
  const form = e.target as HTMLElement | null;
  if (!form || !(form instanceof HTMLFormElement)) return;
  if (!form.hasAttribute('novalidate')) return;

  form.classList.add('loading');

  if (!validateForm(form)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    form.classList.add('was-validated');
    form.classList.remove('loading');

    // Reset Alpine loading state so the spinner is replaced back by the submit label
    const alpineData = (window as any).Alpine?.$data(form);
    if (alpineData && 'loading' in alpineData) alpineData.loading = false;
  }
};

export const initFormValidate = (): void => {
  document.documentElement.addEventListener('submit', onDocumentSubmit);
};
